import { WeightUnit } from "@redotech/crdb-enums/product/weight-unit";
import { useHandler } from "@redotech/react-util/hook";
import {
  OmsInsuranceType,
  SetShippingOptionsActionStep,
} from "@redotech/redo-flows-common/oms/actions";
import {
  CARRIER_HAZMAT,
  Hazmat,
  HAZMAT_DISPLAY_LABELS,
  isSupportedHazmat,
} from "@redotech/redo-fulfillment-carrier-common/hazmat";
import { WeightFormat } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import { Weight } from "@redotech/redo-fulfillment-outbound-labels-common/util";
import {
  CANADA_POST_OPTION_LABELS,
  CanadaPostOption,
  includesDhlBpm,
  SpecialRateEligibility,
  withSpecialRateEligibility,
} from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import { useTranslation } from "@redotech/redo-i18n";
import { TotalWeightWidget } from "@redotech/redo-merchant-app-common/components/weight-widget/total-weight-widget";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  CheckboxValue,
  RedoCheckbox,
} from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoExternalLink } from "@redotech/redo-web/arbiter-components/links/redo-external-link";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useContext } from "react";
import { PartialWithRequiredType } from ".";

export const ShippingOptionsInput = memo(function ShippingOptionsInput({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<SetShippingOptionsActionStep>;
  setStep: (
    step: PartialWithRequiredType<SetShippingOptionsActionStep>,
  ) => void;
}) {
  const { t } = useTranslation();
  const team = useContext(TeamContext);
  // Handler for signature required
  const handleSignatureRequired = (value: CheckboxValue) => {
    if (typeof value === "boolean") {
      if (value === true) {
        setStep({
          ...step,
          shippingOptions: {
            ...step.shippingOptions,
            signatureRequired: true,
            adultSignatureRequired: false,
          },
        });
      } else {
        setStep({
          ...step,
          shippingOptions: {
            ...step.shippingOptions,
            signatureRequired: false,
          },
        });
      }
    }
  };

  // Handler for adult signature required
  const handleAdultSignatureRequired = (value: CheckboxValue) => {
    if (value === true) {
      setStep({
        ...step,
        shippingOptions: {
          ...step.shippingOptions,
          adultSignatureRequired: true,
          signatureRequired: false,
        },
      });
    } else {
      setStep({
        ...step,
        shippingOptions: {
          ...step.shippingOptions,
          adultSignatureRequired: false,
        },
      });
    }
  };

  const handleInsurance = (value: CheckboxValue) => {
    if (typeof value === "boolean") {
      setStep({
        ...step,
        shippingOptions: {
          ...step.shippingOptions,
          includeInsurance: value,
          insuranceType: value
            ? (step.shippingOptions?.insuranceType ??
              OmsInsuranceType.PERCENTAGE)
            : null,
          insurancePercentage: value
            ? (step.shippingOptions?.insurancePercentage ?? 100)
            : null,
          insuranceFixedAmount: value
            ? step.shippingOptions?.insuranceFixedAmount
            : null,
        },
      });
    }
  };

  const handleInsuranceType = ({
    item,
  }: {
    item: { value: OmsInsuranceType };
  }) => {
    setStep({
      ...step,
      shippingOptions: {
        ...step.shippingOptions,
        insuranceType: item.value,
        insurancePercentage:
          item.value === OmsInsuranceType.PERCENTAGE
            ? (step.shippingOptions?.insurancePercentage ?? 100)
            : step.shippingOptions?.insurancePercentage,
        insuranceFixedAmount:
          item.value === OmsInsuranceType.FIXED
            ? (step.shippingOptions?.insuranceFixedAmount ?? null)
            : step.shippingOptions?.insuranceFixedAmount,
      },
    });
  };

  const handleInsurancePercentage = useHandler((value: string) => {
    const parsed = parseFloat(value);
    const clamped = Number.isFinite(parsed)
      ? Math.min(Math.max(parsed, 0), 100)
      : null;
    setStep({
      ...step,
      shippingOptions: {
        ...step.shippingOptions,
        insurancePercentage: clamped,
      },
    });
  });

  const handleInsuranceFixedAmount = useHandler((value: string) => {
    const parsed = parseFloat(value);
    setStep({
      ...step,
      shippingOptions: {
        ...step.shippingOptions,
        insuranceFixedAmount: Number.isFinite(parsed) ? parsed : null,
      },
    });
  });

  // Handler for Media Mail eligibility
  const handleMediaMailEligibility = (value: CheckboxValue) => {
    if (typeof value === "boolean") {
      setStep({
        ...step,
        shippingOptions: {
          ...step.shippingOptions,
          specialRateEligibility:
            withSpecialRateEligibility(
              step.shippingOptions?.specialRateEligibility,
              SpecialRateEligibility.MEDIA_MAIL,
              value,
            ) ?? undefined,
        },
      });
    }
  };

  const handleDhlBpmEligibility = (value: CheckboxValue) => {
    if (typeof value === "boolean") {
      setStep({
        ...step,
        shippingOptions: {
          ...step.shippingOptions,
          specialRateEligibility:
            withSpecialRateEligibility(
              step.shippingOptions?.specialRateEligibility,
              SpecialRateEligibility.DHL_BPM,
              value,
            ) ?? undefined,
        },
      });
    }
  };

  const handleOverrideResidentialAddress = (value: CheckboxValue) => {
    if (typeof value === "boolean") {
      setStep({
        ...step,
        shippingOptions: {
          ...step.shippingOptions,
          overrideResidentialAddress: value,
        },
      });
    }
  };

  const handleCanadaPostOption = (value: CanadaPostOption | null) => {
    setStep({
      ...step,
      shippingOptions: { ...step.shippingOptions, canadaPostOption: value },
    });
  };

  const handleHazmatToggle = (value: boolean) => {
    setStep({
      ...step,
      shippingOptions: {
        ...step.shippingOptions,
        hazmat: value ? Hazmat.CLASS_9_UNMARKED_LITHIUM : null,
      },
    });
  };

  const handleHazmatTypeChange = (value: Hazmat) => {
    setStep({
      ...step,
      shippingOptions: {
        ...step.shippingOptions,
        hazmat: value,
        // Set default dry ice weight when switching to dry ice
        dryIceWeight:
          value === Hazmat.CLASS_9_DRY_ICE
            ? step.shippingOptions?.dryIceWeight || {
                unit: WeightUnit.Oz,
                value: 6,
              }
            : undefined,
      },
    });
  };

  // Handler for dry ice weight changes
  const handleDryIceWeightChange = useHandler((weight: Weight) => {
    setStep({
      ...step,
      shippingOptions: { ...step.shippingOptions, dryIceWeight: weight },
    });
  });

  // Handler for Library Mail eligibility
  /*
  const handleLibraryMailEligibility = (value: CheckboxValue) => {
    if (typeof value === "boolean") {
      setStep({
        ...step,
        shippingOptions: {
          ...(step.shippingOptions || {}),
          specialRateEligibility: value
            ? // If the box is checked, the option should always include library mail
              includesMediaMail(step.shippingOptions?.specialRateEligibility)
              ? SpecialRateEligibility.MEDIA_MAIL_AND_LIBRARY_MAIL
              : SpecialRateEligibility.LIBRARY_MAIL
            : // If the box is unchecked, we leave the media mail option (if it's already included)
              includesMediaMail(step.shippingOptions?.specialRateEligibility)
              ? SpecialRateEligibility.MEDIA_MAIL
              : undefined,
        },
      });
    }
  };
  */

  return (
    <Flex dir="column" gap="md" mt="md">
      <RedoCheckbox
        description={t("Require a signature upon delivery.")}
        label={t("Signature required")}
        setValue={handleSignatureRequired}
        showLabelSpacer
        size="xs"
        value={step.shippingOptions?.signatureRequired ?? false}
      />
      <RedoCheckbox
        description={t("Require an adult signature (18+) upon delivery.")}
        label={t("Adult signature required")}
        setValue={handleAdultSignatureRequired}
        size="xs"
        value={step.shippingOptions?.adultSignatureRequired ?? false}
      />
      <RedoCheckbox
        description={t(
          "Purchase additional shipping insurance for this order.",
        )}
        label={t("Add additional insurance")}
        setValue={handleInsurance}
        size="xs"
        value={step.shippingOptions?.includeInsurance ?? false}
      />
      {step.shippingOptions?.includeInsurance && (
        <Flex dir="column" gap="sm" ml="xs" pl="4xl">
          <RedoSingleSelectDropdownInput<OmsInsuranceType>
            label={t("Insurance type")}
            options={[
              {
                id: OmsInsuranceType.PERCENTAGE,
                type: "text" as const,
                text: t("Percentage of order value"),
                value: OmsInsuranceType.PERCENTAGE,
              },
              {
                id: OmsInsuranceType.FIXED,
                type: "text" as const,
                text: t("Fixed dollar amount"),
                value: OmsInsuranceType.FIXED,
              },
            ]}
            optionSelected={handleInsuranceType}
            selectedItem={
              step.shippingOptions?.insuranceType === OmsInsuranceType.FIXED
                ? {
                    id: OmsInsuranceType.FIXED,
                    type: "text" as const,
                    text: t("Fixed dollar amount"),
                    value: OmsInsuranceType.FIXED,
                  }
                : {
                    id: OmsInsuranceType.PERCENTAGE,
                    type: "text" as const,
                    text: t("Percentage of order value"),
                    value: OmsInsuranceType.PERCENTAGE,
                  }
            }
            size="xs"
          />
          {(step.shippingOptions?.insuranceType ??
            OmsInsuranceType.PERCENTAGE) === OmsInsuranceType.PERCENTAGE && (
            <RedoTextInput
              inputTypeHint="number"
              label={t("Coverage percentage")}
              max={100}
              min={0}
              setValue={handleInsurancePercentage}
              size="sm"
              suffix="%"
              value={String(step.shippingOptions?.insurancePercentage ?? 100)}
            />
          )}
          {step.shippingOptions?.insuranceType === OmsInsuranceType.FIXED && (
            <RedoTextInput
              inputTypeHint="number"
              label={t("Fixed insured amount")}
              min={0}
              prefix="$"
              setValue={handleInsuranceFixedAmount}
              size="sm"
              value={String(step.shippingOptions?.insuranceFixedAmount ?? "")}
            />
          )}
        </Flex>
      )}
      <Flex dir="column" gap="md" justify="flex-start">
        <RedoToggle
          label={t("This shipment contains hazardous materials")}
          setValue={handleHazmatToggle}
          size="xs"
          value={step.shippingOptions?.hazmat != null}
        />

        {step.shippingOptions?.hazmat != null && (
          <Flex dir="column" gap="md" ml="xs" pl="4xl">
            <RedoSingleSelectDropdownInput<Hazmat>
              label={t("Hazmat type")}
              options={[
                {
                  id: "fedex-dhl-section",
                  type: "section" as const,
                  text: "FedEx / DHL",
                  value: null as unknown as Hazmat,
                },
                ...CARRIER_HAZMAT.FEDEX_DHL.map((hazmat) => ({
                  id: hazmat,
                  type: "text" as const,
                  text: HAZMAT_DISPLAY_LABELS[hazmat],
                  value: hazmat,
                })),
                {
                  id: "usps-section",
                  type: "section" as const,
                  text: "USPS",
                  value: null as unknown as Hazmat,
                },
                ...CARRIER_HAZMAT.USPS.map((hazmat) => ({
                  id: hazmat,
                  type: "text" as const,
                  text: HAZMAT_DISPLAY_LABELS[hazmat],
                  value: hazmat,
                })),
                {
                  id: "all-carriers-section",
                  type: "section" as const,
                  text: t("All Carriers"),
                  value: null as unknown as Hazmat,
                },
                ...CARRIER_HAZMAT.ALL_CARRIERS.map((hazmat) => ({
                  id: hazmat,
                  type: "text" as const,
                  text: HAZMAT_DISPLAY_LABELS[hazmat],
                  value: hazmat,
                })),
              ]}
              optionSelected={({ item }) => {
                if (item?.value && item.type !== "section") {
                  handleHazmatTypeChange(item.value);
                }
              }}
              selectedItem={
                step.shippingOptions?.hazmat &&
                isSupportedHazmat(step.shippingOptions.hazmat)
                  ? {
                      id: step.shippingOptions.hazmat,
                      type: "text" as const,
                      text: HAZMAT_DISPLAY_LABELS[step.shippingOptions.hazmat],
                      value: step.shippingOptions.hazmat,
                    }
                  : undefined
              }
              size="xs"
            />

            {step.shippingOptions?.hazmat === Hazmat.CLASS_9_DRY_ICE && (
              <Flex pl="2xl" w="full">
                <TotalWeightWidget
                  disabled={false}
                  hideScale
                  onWeightChange={handleDryIceWeightChange}
                  title=""
                  weight={
                    step.shippingOptions?.dryIceWeight?.unit &&
                    step.shippingOptions?.dryIceWeight?.value
                      ? {
                          unit: step.shippingOptions?.dryIceWeight?.unit,
                          value: step.shippingOptions?.dryIceWeight?.value,
                        }
                      : { unit: WeightUnit.Oz, value: 6 }
                  }
                  weightFormat={
                    team?.settings?.outboundLabels?.weightFormat ??
                    WeightFormat.IMPERIAL
                  }
                />
              </Flex>
            )}
          </Flex>
        )}
      </Flex>
      <RedoCheckbox
        description={
          <Flex>
            <RedoExternalLink
              text={t("Learn more")}
              url="https://faq.usps.com/s/article/What-is-Media-Mail-Book-Rate"
            />
          </Flex>
        }
        label={t("{{carrier}} Media Mail eligible", { carrier: "USPS" })}
        setValue={handleMediaMailEligibility}
        size="xs"
        value={
          step.shippingOptions?.specialRateEligibility?.includes(
            SpecialRateEligibility.MEDIA_MAIL,
          ) ?? false
        }
      />
      <RedoCheckbox
        description={
          <Flex>
            <RedoExternalLink
              text={t("Learn more")}
              url="https://www.dhl.com/content/dam/dhl/local/us/dhl-ecommerce/documents/pdf/us-ecommerce-smartmail-bpm.pdf.coredownload.pdf"
            />
          </Flex>
        }
        label={t("{{carrier}} Bound Printed Matter eligible", {
          carrier: "DHL",
        })}
        setValue={handleDhlBpmEligibility}
        size="xs"
        value={includesDhlBpm(step.shippingOptions?.specialRateEligibility)}
      />
      <RedoCheckbox
        description={t(
          "Force the destination address to be marked as residential, enabling {{carrier}} Home Delivery and similar residential-only services.",
          { carrier: "FedEx" },
        )}
        label={t("Override residential address")}
        setValue={handleOverrideResidentialAddress}
        size="xs"
        value={step.shippingOptions?.overrideResidentialAddress ?? false}
      />
      <RedoSingleSelectDropdownInput<CanadaPostOption | null>
        label={t("{{carrier}} delivery option", { carrier: "Canada Post" })}
        options={[
          { id: "none", type: "text" as const, text: t("None"), value: null },
          ...Object.values(CanadaPostOption).map((option) => ({
            id: option,
            type: "text" as const,
            text: CANADA_POST_OPTION_LABELS[option],
            value: option,
          })),
        ]}
        optionSelected={({ item }) => {
          handleCanadaPostOption(item.value);
        }}
        selectedItem={
          step.shippingOptions?.canadaPostOption
            ? {
                id: step.shippingOptions.canadaPostOption,
                type: "text" as const,
                text: CANADA_POST_OPTION_LABELS[
                  step.shippingOptions.canadaPostOption
                ],
                value: step.shippingOptions.canadaPostOption,
              }
            : {
                id: "none",
                type: "text" as const,
                text: t("None"),
                value: null,
              }
        }
        size="xs"
      />
      {/* HIDE LIBRARY MAIL FOR NOW */}
      {/* <RedoCheckbox
        description={
          <Flex>
            <RedoExternalLink
              text={t("Learn more")}
              url="https://faq.usps.com/s/article/What-are-Library-Mail-Service-and-Media-Mail-Service"
            />
          </Flex>
        }
        label={t("{{carrier}} Library Mail eligible", { carrier: "USPS" })}
        setValue={handleLibraryMailEligibility}
        size="xs"
        value={
          step.shippingOptions?.specialRateEligibility?.includes(
            SpecialRateEligibility.LIBRARY_MAIL,
          ) ?? false
        }
      /> */}
    </Flex>
  );
});
